import { MoreVert } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { differenceInCalendarDays, isSameDay } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import { resetServerContext } from 'react-beautiful-dnd';
import usePresentations from '../../../../../../../hooks/usePresentations';
import { Meetingroom, Slot } from '../../../../../../../interfaces/interfaces';
import { fetchAndSetSlots, setSlots as setGlobalSlots } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../../../reduxToolkit/typedRedux';
import { formatUTCDate } from '../../../../../../../services/helper';
import { deleteSlot, getMeetings, getSlots } from '../../../../../../../services/services';
import VyButton from '../../../../../../common/VyButton';
import { VyExportButton } from '../../../../../../common/VyExportButton';
import VyModal from '../../../../../../common/VyModal';
import VyPopoverMenu from '../../../../../../common/VyPopoverMenu';
import { TabContentInterface } from '../../../../../pageBuilder/PBBooths';
import PresentationEditComponent from '../PublicPresentation/PresentationEditComponent';
import { CreatePresentationModal } from './CreatePresentationModal';
import PresentationsGroupedView from './PresentationsGroupedView';
import PresentationsListView from './PresentationsListView';

interface SlotGroup {
  key: string;
  slots: Slot[];
  datetime_start: string;
  datetime_end: string;
}

const PresentationsSlots = ({ setActions }: TabContentInterface) => {
  const event = useSelector((state) => state.common.event);

  const [slots, setSlots] = useState<Slot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [unassignedPresentations, setUnassignedPresentations] = useState<Meetingroom[]>([]);
  const [isLoadingUnassignedPresentations, setIsLoadingUnassignedPresentations] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [slotGroups, setSlotGroups] = useState<SlotGroup[]>([]);
  const [newPresentationModalOpen, setNewPresentationModalOpen] = useState<boolean>(false);
  const [unassignedSlots, setUnassignedSlots] = useState<Slot[]>([]);

  const [editPresentationModal, setEditPresentationModal] = useState<Meetingroom>();
  /**
   * this state contains the slotgroups that should have the date text above them
   * without affecting the state structure used to manipulate slots dates and indexes.
   */
  const [markedSlots, setMarkedSlots] = useState<SlotGroup[]>([]);

  const { t } = useTranslation();
  const { updateGroup, assignSlot } = usePresentations();
  const dispatch = useDispatch();

  const unassignedSlotsDiv = useRef<HTMLDivElement>(null);

  async function fetchSlots() {
    if (!event) return;

    setIsLoadingSlots(true);
    try {
      const { data: data } = await getSlots(event._id);

      setSlots(data);
      dispatch(setGlobalSlots(data));
    } catch (err) {
      console.error('Slot fetching error', err);
    }
    setIsLoadingSlots(false);
  }

  async function fetchUnassignedPresentations() {
    if (!event) return;
    setIsLoadingUnassignedPresentations(true);
    try {
      const { data: data } = await getMeetings({
        meetingtype: 'presentation',
        hasSlot: false,
        hasName: true,
        filterEvent: event._id,
        limit: 5000,
      });

      const { data: unassignedStudios } = await getMeetings({
        meetingtype: 'studio',
        isStudioForPresentationSlot: true,
        hasSlot: false,
        filterEvent: event._id,
      });
      setUnassignedPresentations([...data, ...unassignedStudios]);
    } catch (err) {
      console.error(err);
    }
    setIsLoadingUnassignedPresentations(false);
  }

  function fetch(onlyUnassigned = false) {
    if (!onlyUnassigned) fetchSlots();
    fetchUnassignedPresentations();
    dispatch(fetchAndSetSlots(event!._id));
  }

  useEffect(() => {
    if (!event) return;
    setActions([
      <VyButton onClick={() => setNewPresentationModalOpen(true)}>{t('account:new-presentation')}</VyButton>,
      <VyPopoverMenu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        content={() => (
          <div>
            <VyExportButton
              buttonText={t('event:export-unassigned-presentations')}
              axiosMethod={'GET'}
              csvURL={`/meeting/export/${event._id}?meetingtype=presentation&resType=csv&newOnly=true`}
              xlsxURL={`/meeting/export/${event._id}?meetingtype=presentation&resType=xlsx&newOnly=true`}
              altButton={<p className="p-4 cursor-pointer hover:bg-gray-200">{t('account:export-new-presentations')}</p>}
            />
            <VyExportButton
              buttonText={t('event:export-assigned-presentations')}
              axiosMethod={'GET'}
              csvURL={`/slot/getall/${event._id}?resType=csv`}
              xlsxURL={`/slot/getall/${event._id}?resType=xlsx`}
              altButton={<p className="p-4 cursor-pointer hover:bg-gray-200">{t('account:export-existing-presentations')}</p>}
            />
            <VyExportButton
              buttonText={t('account:export-all-presentations')}
              axiosMethod={'GET'}
              csvURL={`/meeting/export/${event._id}?meetingtype=presentation&resType=csv`}
              xlsxURL={`/meeting/export/${event._id}?meetingtype=presentation&resType=xlsx`}
              altButton={<p className="p-4 cursor-pointer hover:bg-gray-200">{t('account:export-all-presentations')}</p>}
            />
            <VyExportButton
              buttonText={t('account:export-all-participants')}
              axiosMethod={'GET'}
              xlsxURL={`/reporting/meetingdetails/exportall/${event._id}?resType=xlsx`}
              altButton={<p className="p-4 cursor-pointer hover:bg-gray-200">{t('account:export-all-participants')}</p>}
            />
            {event?.settings.enableOnlinePresentationBooking && (
              <VyExportButton
                buttonText={t('account:export-all-guestlists')}
                axiosMethod={'GET'}
                xlsxURL={`/reporting/meetingdetails/exportguestlists/${event._id}?resType=xlsx`}
                altButton={<p className="p-4 cursor-pointer hover:bg-gray-200">{t('account:export-all-guestlists')}</p>}
              />
            )}
          </div>
        )}>
        <IconButton>
          <MoreVert />
        </IconButton>
      </VyPopoverMenu>,
    ]);
    fetch();
  }, [event]);

  useEffect(() => {
    if (!slots || !Array.isArray(slots)) return;
    const sortedSlotsByStartTime = slots ? [...slots].sort((a: Slot, b: Slot) => parseInt(b?.datetime_start) - parseInt(a?.datetime_start)) : [];

    const groupedRooms = sortedSlotsByStartTime.reduce((j: any, k: Slot) => {
      const group = `${formatUTCDate(new Date(k.datetime_start), 'dd.MM.yyyy HH:mm')} - ${formatUTCDate(new Date(k.datetime_end), 'dd.MM.yyyy HH:mm')}`;
      j.hasOwnProperty(group) ? j[group].push(k) : (j[group] = [k]);
      return j;
    }, {});

    const _sortedGroupedRooms = sortBySortIndex(groupedRooms);
    updateLocalSlotGroups(_sortedGroupedRooms);
  }, [slots, event]);

  const sortSlotGroup = (slotGroup: SlotGroup[]) =>
    slotGroup.sort((a: SlotGroup, b: SlotGroup) => new Date(a?.datetime_start).getTime() - new Date(b?.datetime_start).getTime());

  const updateLocalSlotGroups = (defaultSlotObject: { [key: string]: Slot[] }) => {
    const _slotgroups: SlotGroup[] = [];
    for (const key in defaultSlotObject) {
      const _slots = defaultSlotObject[key];
      _slotgroups.push({ key, slots: _slots, datetime_start: _slots[0].datetime_start, datetime_end: _slots[0].datetime_end });
    }

    //Copy existing slot group templates
    const _slotgroupsWithExisting = [
      ..._slotgroups,
      ...slotGroups.filter((group) => group.slots.length === 0 && !_slotgroups.map((g) => g.key).includes(group.key)),
    ];
    setSlotGroups(sortSlotGroup(_slotgroupsWithExisting));
    const _slotGroups = [
      ...sortSlotGroup(_slotgroupsWithExisting)
        .slice()
        .sort((a, b) => parseInt(a.datetime_start) - parseInt(b.datetime_start)),
    ];
    setMarkedSlots([_slotGroups[0]]);
    _slotGroups.forEach((el, i) => {
      if (i === 0) return;
      const currDate = new Date(el.datetime_start);
      const prevDate = new Date(_slotGroups[i - 1].datetime_start);
      if (differenceInCalendarDays(currDate, prevDate) > 0) setMarkedSlots((state) => [...state, el]);
    });
  };

  useEffect(() => {
    const _slots = unassignedPresentations.map((p, index) => {
      const _slot: Slot = {
        sortIndex: index,
        datetime_start: 'no date',
        datetime_end: 'no date',
        status: 'unassigned',
        _id: 'unassigned-' + index,
        datetime_hostnotification_sent: 'no date',
        meetingroom: p,
      };
      return _slot;
    });

    setUnassignedSlots(_slots);
  }, [unassignedPresentations]);

  //reorder both groups (the source & the destination group)
  function reorder(source: Slot[], destination: Slot[], sourceIndex: number, destIndex: number, destDroppableId: string) {
    let sourceArr: Slot[] = Array.from(source || []);
    let destArr: Slot[] = Array.from(destination || []);
    const [removed] = sourceArr.splice(sourceIndex, 1);
    let newSlot = { ...removed };
    if (destDroppableId !== 'unassigned-slots') {
      const _slotGroup = slotGroups.find((sg) => sg.key === destDroppableId);
      if (!_slotGroup) return { newSource: source, newDestination: destination };
      newSlot = { ...removed, datetime_start: _slotGroup.datetime_start, datetime_end: _slotGroup.datetime_end };
    }
    destArr.splice(destIndex, 0, newSlot);
    sourceArr = sourceArr.map((s, index) => {
      return { ...s, sortIndex: index };
    });
    destArr = destArr.map((s, index) => {
      return { ...s, sortIndex: index };
    });
    return { newSource: sourceArr, newDestination: destArr };
  }

  //reorder the slots of the same group
  function sameLevelReorder(slots: Slot[], sourceIndex: number, destIndex: number, destDroppableId: string) {
    const slotsArr: Slot[] = Array.from(slots);
    const [removed] = slotsArr.splice(sourceIndex, 1);
    const _slotGroup = slotGroups.find((sg) => sg.key === destDroppableId);
    if (!_slotGroup) return slots;
    const newSlot = { ...removed, datetime_start: _slotGroup.datetime_start, datetime_end: _slotGroup.datetime_end };
    // I needed to create new array instance because splicing the same variable more than once caused weird bugs
    let newSlotsArr = [...slotsArr];
    newSlotsArr.splice(destIndex, 0, newSlot);
    newSlotsArr = newSlotsArr.map((s, index) => {
      return { ...s, sortIndex: index };
    });

    return newSlotsArr;
  }

  /**
   * remove a slot from a group.
   * when dragging a slot to the unassigned group, it will be deleted from the slots collection in the database
   * @param id
   */
  async function removeSlot(id: string) {
    try {
      await deleteSlot(id);
      fetch();
    } catch (error) {
      console.error(error);
    }
  }

  // this function is used to make group names which are used as keys or to be displayed
  // if it's used as a key (forDisplay = false) the value always include the full date
  function makeGroupName(datetime_start: string, datetime_end: string, forDisplay?: boolean) {
    const sameDay = isSameDay(new Date(datetime_start), new Date(datetime_end));
    const dateFormat = sameDay && forDisplay ? 'HH:mm' : 'dd.MM.yyyy HH:mm';
    const datesSeparator = sameDay ? ' - ' : `<p>${t('event:to')}</p>`;
    return `${formatUTCDate(new Date(datetime_start), dateFormat)}${datesSeparator}${formatUTCDate(new Date(datetime_end), dateFormat)}`;
  }
  const onDragEnd = async (result: any) => {
    const { source, destination } = result;
    // if there's no destination OR the destination is the same as the source with the same index, then no need to update anything
    if (!result.destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }
    const _sourceGroupIndex = slotGroups.map((g) => g.key).indexOf(source.droppableId);
    const _destinationGroupIndex = slotGroups.map((g) => g.key).indexOf(destination.droppableId);
    // if there's an interaction with the unassigned group, the behaviour is different
    if (source.droppableId === 'unassigned-slots' || destination.droppableId === 'unassigned-slots') {
      /**
       * if the source is not a part of the interaction, meaning the dragging is made from an already assigned
       * to the unassigned. So the behaviour is removing the slot
       */
      if (source.droppableId !== 'unassigned-slots') {
        const { newSource, newDestination } = reorder(
          slotGroups[_sourceGroupIndex].slots,
          unassignedSlots,
          source.index,
          destination.index,
          destination.droppableId
        );

        setUnassignedSlots(newDestination);
        const _slotGroups = [...slotGroups];
        _slotGroups[_sourceGroupIndex] = { ..._slotGroups[_sourceGroupIndex], slots: newSource };
        setSlotGroups(sortSlotGroup(_slotGroups));

        await removeSlot(slotGroups[_sourceGroupIndex].slots[source.index]._id);
        await updateGroup(newSource, event!._id);
      } else if (destination.droppableId !== 'unassigned-slots') {
        /**
         * if the destination is not a part of the interaction, meaning the dragging is made from an unassigned
         * then out. So the behaviour is assigning the slot
         */
        const { newSource, newDestination } = reorder(
          unassignedSlots,
          slotGroups[_destinationGroupIndex].slots,
          source.index,
          destination.index,
          destination.droppableId
        );
        setUnassignedSlots(newSource);
        //let _defaultSlots = { ...defaultSlots, [destination.droppableId]: newDestination };
        const _slotGroups = [...slotGroups];
        _slotGroups[_destinationGroupIndex] = { ..._slotGroups[_destinationGroupIndex], slots: newDestination };
        setSlotGroups(sortSlotGroup(_slotGroups));
        await assignSlot(newDestination[destination.index]);
        fetch();
        await updateGroup(
          newDestination.filter((s: Slot) => s.status !== 'unassigned'),
          event!._id
        );
      } else {
        /**
         * if both source and destination are part of the interaction, then it's a same level reorder.
         * this won't have any effect since it's just reordering unassigned meetingrooms
         */
        const result = sameLevelReorder(unassignedSlots, source.index, destination.index, destination.droppableId);
        setUnassignedSlots(result);
      }
    }
    // interaction between slot groups or only one group itself
    else {
      /**
       * if the interaction is between different slot groups
       * reorder then with the new correctly ordered lists, we update the state
       * we also update the database with the new data of the dragged slot and the indexes of the slots affected
       */
      if (source.droppableId !== destination.droppableId) {
        const { newSource, newDestination } = reorder(
          slotGroups[_sourceGroupIndex].slots,
          slotGroups[_destinationGroupIndex].slots,
          source.index,
          destination.index,
          destination.droppableId
        );
        //let _defaultSlots: any = { ...defaultSlots, [destination.droppableId]: newDestination, [source.droppableId]: newSource };
        const _slotGroups = [...slotGroups];
        _slotGroups[_sourceGroupIndex] = { ..._slotGroups[_sourceGroupIndex], slots: newSource };
        _slotGroups[_destinationGroupIndex] = { ..._slotGroups[_destinationGroupIndex], slots: newDestination };

        setSlotGroups(sortSlotGroup(_slotGroups));
        await updateGroup([...newSource, ...newDestination], event!._id);
        fetch();
      } else {
        /**
         * if the source group is the same as the destination
         * it's a same level reorder
         * we reorder and update the local state
         * then we update the database with the new indexes
         */
        const result = sameLevelReorder(slotGroups[_sourceGroupIndex].slots, source.index, destination.index, destination.droppableId);
        //let _defaultSlots = { ...defaultSlots, [source.droppableId]: result };
        const _slotGroups = [...slotGroups];
        _slotGroups[_sourceGroupIndex] = { ..._slotGroups[_sourceGroupIndex], slots: result };
        setSlotGroups(sortSlotGroup(_slotGroups));
        await updateGroup(result, event!._id);
      }
    }
  };

  /**
   * sorting an object of grouped rooms by sortIndex
   * we use this everytime we update the local state to have a persistent sorted list UI wise
   * as well as ensure the reordering works correctly.
   */
  const sortBySortIndex = (groupedRooms: { [key: string]: Slot[] }) => {
    const _groupedRooms = { ...groupedRooms };
    for (const [key, value] of Object.entries(groupedRooms)) {
      _groupedRooms[key] = value.slice().sort((a: Slot, b: Slot) => a.sortIndex - b.sortIndex);
    }
    return _groupedRooms;
  };

  const onSlotGroupCreate = (time: { datetime_start: string; datetime_end: string }, withFetch?: boolean) => {
    const _slotGroups = [...slotGroups];

    const _key = `${formatUTCDate(new Date(time.datetime_start), 'dd.MM.yyyy HH:mm')} - ${formatUTCDate(new Date(time.datetime_end), 'dd.MM.yyyy HH:mm')}`;
    _slotGroups.push({ key: _key, datetime_start: time.datetime_start, datetime_end: time.datetime_end, slots: [] });
    setSlotGroups(sortSlotGroup([..._slotGroups]));
    withFetch && fetch();
  };

  const onSlotGroupDelete = (key: string) => {
    setSlotGroups(slotGroups.filter((g) => g.key !== key));
  };

  const tabButton = (title: string, index: 0 | 1) => (
    <button
      style={{
        padding: '3px 5px',
        border: '1px solid var(--vystem-blue)',
        background: selectedTab === index ? 'var(--vystem-blue)' : 'white',
        color: selectedTab === index ? 'white' : 'var(--vystem-blue)',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
      }}
      className={index === 0 ? 'rounded-l' : 'rounded-r'}
      onClick={() => setSelectedTab(index)}>
      {title}
    </button>
  );

  resetServerContext();

  return (
    <>
      <div className="flex flex-row justify-center text-xl ">
        {tabButton(t('account:slots.grouped-view'), 0)}
        {tabButton(t('account:slots.list-view'), 1)}
      </div>
      {selectedTab === 0 && (
        <PresentationsGroupedView
          onEditPresentation={setEditPresentationModal}
          slotGroups={slotGroups}
          markedSlots={slotGroups}
          isLoadingSlots={isLoadingSlots}
          isLoadingUnassignedPresentations={isLoadingUnassignedPresentations}
          unassignedSlots={unassignedSlots}
          unassignedSlotsDiv={unassignedSlotsDiv}
          fetch={() => fetch()}
          makeGroupName={makeGroupName}
          onSlotGroupCreate={onSlotGroupCreate}
          onSlotGroupDelete={onSlotGroupDelete}
          onDragEnd={onDragEnd}
        />
      )}
      {selectedTab === 1 && (
        <PresentationsListView
          slotGroups={slotGroups}
          eventid={event!._id}
          markedSlots={markedSlots}
          unassignedSlots={unassignedSlots}
          fetch={() => fetch()}
          makeGroupName={makeGroupName}
          onSlotGroupCreate={onSlotGroupCreate}
          onSlotGroupDelete={onSlotGroupDelete}
          onDragEnd={onDragEnd}
          onEditPresentation={setEditPresentationModal}
        />
      )}
      {newPresentationModalOpen && (
        <CreatePresentationModal
          open={newPresentationModalOpen}
          onClose={() => setNewPresentationModalOpen(false)}
          onCreate={() => {
            fetchUnassignedPresentations();
            dispatch(fetchAndSetSlots(event!._id));
          }}
        />
      )}
      <VyModal
        open={!!editPresentationModal}
        onClose={() => {
          setEditPresentationModal(undefined);
          fetch();
        }}
        disableDefaultStyling
        style={{
          width: '100%',
          overflow: 'hidden',
          backgroundImage: 'linear-gradient(20deg, rgba(0,0,0,0.8) 0%, rgba(150,150,150,0.60) 63%, rgba(238,238,238,0.44) 100%)',
        }}
        hasDefaultPadding={false}>
        {() => <PresentationEditComponent meetingroom={editPresentationModal!} />}
      </VyModal>
    </>
  );
};
export default PresentationsSlots;
